import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Sparkles, Copy, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { isFreedomCampaignActive } from '../lib/campaign';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = 'ss_welcome_popup_shown';
const SHOW_AFTER_MS = 2500;

/**
 * Landing-page welcome popup promoting the active coupon (WELCOME10 by default).
 * Shows once per browser session ~2.5s after the homepage loads. Pulls the active
 * coupon from /api/coupons/active so admin edits propagate automatically.
 * Skips returning customers who already redeemed the coupon (suppression check).
 */
export const WelcomePopup = () => {
  const [open, setOpen] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [alreadyUsed, setAlreadyUsed] = useState(false);
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useCustomerAuth();

  // Only arm on the homepage AND when the Independence Day campaign isn't running
  // (during the campaign, FreedomSalePopup takes over so the two never collide).
  const onHomepage = location.pathname === '/' && !isFreedomCampaignActive();

  // Fetch active coupon (cheap — cached by browser)
  useEffect(() => {
    if (!onHomepage) return;
    let cancelled = false;
    axios.get(`${API}/coupons/active`, { timeout: 5000 })
      .then((r) => { if (!cancelled) setCoupon(r.data?.coupon || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [onHomepage]);

  // For authenticated customers, check if they've already redeemed this coupon.
  // Anonymous visitors are always treated as eligible.
  useEffect(() => {
    if (!onHomepage || !coupon || !token) return;
    let cancelled = false;
    axios.get(`${API}/coupons/used?code=${encodeURIComponent(coupon.code)}`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 5000,
    })
      .then((r) => { if (!cancelled) setAlreadyUsed(!!r.data?.used); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [onHomepage, coupon, token]);

  // Open after a short delay, only once per session, only if the user hasn't already redeemed.
  useEffect(() => {
    if (!onHomepage) return undefined;
    if (!coupon) return undefined;
    if (alreadyUsed) return undefined;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return undefined;
    const t = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, '1');
      setOpen(true);
    }, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [coupon, onHomepage, alreadyUsed]);

  if (!open || !coupon) return null;

  const close = () => setOpen(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      localStorage.setItem('ss_applied_coupon', coupon.code);
      setCopied(true);
      toast.success(`Code ${coupon.code} copied`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Copy failed — long-press to select the code');
    }
  };

  const shopNow = () => {
    localStorage.setItem('ss_applied_coupon', coupon.code);
    close();
    const el = document.querySelector('[data-testid="featured-products-section"]');
    if (el) {
      const offset = window.innerWidth < 768 ? 110 : 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      navigate('/search?sort=newest');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
      data-testid="welcome-popup"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div
        className="relative max-w-md w-full bg-[#FAF9F7] rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          data-testid="welcome-popup-close"
          aria-label="Close"
          className="absolute top-3 right-3 z-10 text-[#FAF9F7]/80 hover:text-white transition"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header band */}
        <div className="relative bg-gradient-to-br from-[#6B4E37] via-[#8B6F4E] to-[#C4B5A0] text-[#FAF9F7] px-6 pt-8 pb-10 text-center overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 30%, rgba(255,255,255,.4) 0px, transparent 80px), radial-gradient(circle at 80% 70%, rgba(255,255,255,.3) 0px, transparent 90px)',
            }}
          />
          <Sparkles className="relative h-8 w-8 mx-auto mb-3 text-[#FAF6EE]" />
          <p className="relative font-montserrat text-[11px] tracking-[0.35em] uppercase opacity-90">
            {coupon.urgency_text || 'Limited-time online launch offer'}
          </p>
          <h2 className="relative font-seasons text-3xl sm:text-4xl mt-2 leading-tight">
            Welcome to <br className="sm:hidden" />Sachdeva Sons
          </h2>
          <p className="relative font-montserrat text-sm mt-3 text-[#FAF6EE]">
            Enjoy a special introductory offer on your first order.
          </p>
        </div>

        {/* Code block */}
        <div className="px-6 -mt-5 relative">
          <div className="bg-white border-2 border-dashed border-[#C4B5A0] rounded-lg py-4 px-3 text-center shadow-md">
            <p className="font-montserrat text-[10px] tracking-[0.3em] uppercase text-[#8B7D6B]">Use code</p>
            <div className="font-seasons text-4xl text-[#6B4E37] tracking-wider mt-1">{coupon.code}</div>
            <p className="font-montserrat text-xs text-[#8B7D6B] mt-1">
              {coupon.discount_percent}% OFF · Min order ₹{(coupon.min_order_value || 0).toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        {/* CTAs */}
        <div className="px-6 py-5 space-y-2">
          <Button
            onClick={shopNow}
            data-testid="welcome-popup-shop"
            className="w-full bg-[#6B4E37] hover:bg-[#5A4030] text-white font-montserrat tracking-[0.2em] uppercase text-xs py-6"
          >
            Shop Now <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            onClick={copyCode}
            variant="outline"
            data-testid="welcome-popup-copy"
            className="w-full border-[#C4B5A0] text-[#6B4E37] hover:bg-[#FAF6EE] font-montserrat tracking-[0.2em] uppercase text-xs py-6"
          >
            {copied ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? 'Code copied' : `Copy ${coupon.code}`}
          </Button>
          <button
            type="button"
            onClick={close}
            data-testid="welcome-popup-skip"
            className="block w-full text-[11px] font-montserrat text-[#8B7D6B] hover:text-[#2C2C2C] mt-2 underline-offset-2 hover:underline"
          >
            No thanks, I'll skip the discount
          </button>
        </div>
      </div>
    </div>
  );
};
